import router from "next/router";
import {
  Context,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { fetchAPI, HTTP_METHOD } from "../includes/fetch";
import { routerPushByUserType } from "../includes/utils";
import { FriendRequest } from "../types/FriendRequest";
import { ServiceProvider } from "../types/ServiceProvider";
import {
  SidebarContact,
  UnreadMessageRooms,
  UnreadTripNotifications,
  User,
  UserTypes,
} from "../types/User";
import { useAppState } from "./appState";

// The inital auth response we get from laravel auth
export type Identity = {
  access_token: string;
  token_type: string;
  expires_in: Number;
  formattedType: UserTypes;
};

export type AuthType = {
  isAuthenticated: boolean;
  identity?: Identity;
  me?: MeType;
  friends: SidebarContact[];
  fetchFriendsList: () => Promise<void>;
  pendingFriendRequests: FriendRequest[];
  fetchPendingFriendRequests: () => Promise<void>;
  setMe(me): void;
  loading: boolean;
  login(email: string, password: string, redirectRoute?: string): any;
  smsLogin(
    phone: string,
    code: string,
    pushFunction?,
    redirectRoute?: string
  ): any;
  logout(): void;
  isAdmin(): boolean;
  fetchMe(): Promise<MeType>;
  fetchMyServices(force?: Boolean);
  GetMessageCountForContext(): number;
  GetTripNotificationCountForContext(): number;
  ameegoTripInviteCount?: number;
  fetchAmeegoTripInviteCount(): Promise<void>;
};

export type MeType = User & {
  formattedType: string;
  stripe_onboarded: boolean;
  uncheckedBookingsCount: number;
  unreadRooms: UnreadMessageRooms;
  unreadTripNotifications: UnreadTripNotifications;
  unreadNotifications: number;
  balanceFloat: string;
  services?: ServiceProvider[];
};

const AuthContext: Context<AuthType | null> = createContext(null);

export const AuthProvider = ({ children }) => {
  const [identity, setIdentity]: [
    identity: Identity,
    setIdentity: Dispatch<SetStateAction<Identity>>
  ] = useState();
  const [me, setMe]: [me: MeType, setMe: Dispatch<SetStateAction<MeType>>] =
    useState();

  const [friends, setFriends] = useState<SidebarContact[]>([]);
  const [pendingFriendRequests, setPendingFriendRequests] = useState<
    FriendRequest[]
  >([]);
  const [ameegoTripInviteCount, setAmeegoTripInviteCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [cookie, setCookie] = useCookies(["identity", "token"]);

  const { GetUserTypeInt } = useAppState();

  const refresh = async () => {
    const res = await fetchAPI<Identity>("/api/auth/refresh", HTTP_METHOD.POST);

    if (!res.ok) {
      // Something went wrong lets not refresh again
      window.localStorage.setItem("canRefresh", "false");
      return;
    }

    const response = await res.json();

    if (response.access_token) {
      setIdentity(response);
      let oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

      setCookie("token", response.access_token, {
        path: "/",
        maxAge: 31556952, // Expires after 1hr
        sameSite: true,
        httpOnly: false,
        expires: oneYearFromNow,
      });
    }
  };

  const parseLoginResponse = async (
    res,
    redirectRoute?: string,
    pushFunction?
  ) => {
    const response = await res.json();
    if (response.access_token) {
      let oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

      setCookie("token", response.access_token, {
        path: "/",
        maxAge: 31556952, // Expires after 1hr
        sameSite: true,
        httpOnly: false,
        expires: oneYearFromNow,
      });
      window.localStorage.setItem("canRefresh", "true");

      // Set identity first
      setIdentity(response);

      // Redirects after login
      if (!pushFunction) {
        // Use the redirect parameter passed directly
        if (redirectRoute) {
          // Basic validation: only allow internal relative URLs
          const VALID_PATTERNS = [
            /^\/app\/post\/\d+$/,
            /^\/app\/ameego\/\d+$/,
            /^\/service\/\d+$/,
            /^\/service\/\d+\/deal\/\d+$/,
          ];

          const isValidRedirect =
            redirectRoute &&
            typeof redirectRoute === "string" &&
            VALID_PATTERNS.some((pattern) => pattern.test(redirectRoute));

          if (isValidRedirect) {
            router.push(decodeURIComponent(redirectRoute));
          } else {
            routerPushByUserType(response.formattedType);
          }
        } else {
          routerPushByUserType(response.formattedType);
        }
      } else {
        pushFunction();
      }
    }
    return response;
  };

  const login = async (
    email: string,
    password: string,
    redirectRoute?: string
  ) => {
    const res = await fetchAPI("/api/auth/login", HTTP_METHOD.POST, {
      email,
      password,
    });

    return await parseLoginResponse(res, redirectRoute);
  };

  const smsLogin = async (
    phone,
    code,
    pushFunction,
    redirectRoute?: string
  ) => {
    const request = await fetchAPI("/api/auth/sms-auth", HTTP_METHOD.POST, {
      phone: phone,
      auth_code: code,
    });

    return await parseLoginResponse(request, redirectRoute, pushFunction);
  };

  const logout = async () => {};

  const fetchMe = async () => {
    if (!window.navigator.onLine) return;

    try {
      const res = await fetchAPI<MeType>("/api/auth/me", HTTP_METHOD.POST);

      if (res.ok) {
        const me = await res.json();
        window.localStorage.setItem(
          "valmeego",
          Buffer.from(
            JSON.stringify({
              me,
              identity,
            }),
            "binary"
          ).toString("base64")
        );
        setMe(me);
        return me;
      }
    } finally {
      setLoading(false);
    }
  };

  // Only fetch it once if it hasnt been set or allow force refresh
  const fetchMyServices = async (force = false) => {
    if (!me.services || force) {
      const res = await fetchAPI<ServiceProvider[]>(
        "/api/serviceProvider/my-services"
      );
      setMe({
        ...me,
        services: await res.json(),
      });
    }
  };

  const fetchFriendsList = async () => {
    const res = await fetchAPI<SidebarContact[]>("/api/auth/me/friends");

    if (res.ok) {
      setFriends(await res.json());
    }
  };

  const fetchPendingFriendRequests = async () => {
    const res = await fetchAPI<FriendRequest[]>("/api/auth/me/friends/pending");

    if (res.ok) {
      setPendingFriendRequests(await res.json());
    }
  };

  const fetchAmeegoTripInviteCount = async () => {
    const response = await fetchAPI<{
      invites: [];
    }>(`/api/ameego/invites`);

    if (response.status == 200) {
      const ameegoTrips = await response.json();
      setAmeegoTripInviteCount(ameegoTrips.invites.length);
    } else {
      setAmeegoTripInviteCount(0);
    }
  };

  const GetMessageCountForContext = () => {
    if (!me.unreadRooms.contexts) return;

    if (me.unreadRooms.contexts[GetUserTypeInt()]) {
      /**
       * for when this needs to be reset again cause its more confusing now
       * https://github.com/valmeego/mvp/issues/1282
       *
       * return Object.keys(me.unreadRooms.contexts[GetUserTypeInt()]).length;
       */

      return me.unreadRooms.contexts[GetUserTypeInt()].reduce(
        (acc, item) => acc + item.unread_count,
        0
      );
    }

    return 0;
  };

  const GetTripNotificationCountForContext = () => {
    if (me?.unreadTripNotifications?.contexts[GetUserTypeInt()]) {
      return me.unreadTripNotifications.contexts[GetUserTypeInt()].reduce(
        (acc, item) => acc + item.unread_count,
        0
      );
    } else return 0;
  };

  useEffect(() => {
    const initAuth = async () => {
      const canRefresh = window.localStorage.getItem("canRefresh") === "true";

      if (canRefresh && !identity) {
        await refresh();
        return; // Let it run again when identity is set
      }

      if (canRefresh && identity) {
        await fetchMe();
        if (GetUserTypeInt() === UserTypes.AMEEGO) {
          fetchAmeegoTripInviteCount();
        }
      } else {
        // User is not authenticated or canRefresh is false
        setLoading(false);
      }
    };

    initAuth();
  }, [identity]);

  useEffect(() => {
    if (me && !me.services) {
      fetchMyServices();
    }
  }, [me]);

  const isAdmin = () => {
    if (me && me.formattedType === "admin") return true;

    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!identity,
        identity,
        me,
        friends,
        fetchFriendsList,
        pendingFriendRequests,
        fetchPendingFriendRequests,
        setMe,
        login,
        smsLogin,
        loading,
        fetchMe,
        fetchMyServices,
        logout,
        isAdmin,
        GetMessageCountForContext,
        GetTripNotificationCountForContext,
        ameegoTripInviteCount,
        fetchAmeegoTripInviteCount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
